const CMS_URL=import.meta.env.VITE_BPAS_CMS_URL;

const generateSessionId=()=> {
  const stored=localStorage.getItem('bpas_session_id');
  if (stored) return stored;
  const newId=`sess_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
  localStorage.setItem('bpas_session_id',newId);
  return newId;
};

const getSessionId=()=> generateSessionId();

const debounceMap=new Map();
const throttleMap=new Map();
const lastCmsSyncRef={success: 0,failure: 0};

const debounce=(key,fn,delay=2000)=> {
  if (debounceMap.has(key)) {
    clearTimeout(debounceMap.get(key));
  }
  debounceMap.set(
    key,
    setTimeout(()=> {
      fn();
      debounceMap.delete(key);
    },delay)
  );
};

const throttle=(key,fn,delay=5000)=> {
  const now=Date.now();
  const last=throttleMap.get(key) || 0;
  if (now - last < delay) return;
  throttleMap.set(key,now);
  fn();
};

const getBasePayload=(overrides={})=> {
  const sessionId=getSessionId();
  return {
    sessionId,
    eventTime: new Date().toISOString(),
    pagePath: window.location.pathname,
    referrer: document.referrer || 'direct',
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    source: 'portfolio.bpasolutions.au',
    ...overrides,
  };
};

const trackEvent=async (eventType,payload={})=> {
  if (!CMS_URL) return;
  const eventPayload={
    type: 'activity',
    eventType,
    ...getBasePayload(),
    ...payload,
  };
  const data=new FormData();
  data.append('payload',JSON.stringify(eventPayload));
  if (navigator.sendBeacon) {
    navigator.sendBeacon(CMS_URL,data);
  } else {
    try {
      await fetch(CMS_URL,{
        method: 'POST',
        body: data,
        keepalive: true,
      });
    } catch (e) {
      // Fail silently to avoid user-facing errors
    }
  }
};

export const trackPageView=()=> {
  trackEvent('page_view');
};

export const trackCmsSync=(success,dataChanged=false)=> {
  const now=Date.now();
  const lastRef=success ? lastCmsSyncRef.success : lastCmsSyncRef.failure;
  const minInterval=5 * 60 * 1000;

  if (now - lastRef < minInterval && !dataChanged) {
    return;
  }

  if (success) {
    lastCmsSyncRef.success=now;
  } else {
    lastCmsSyncRef.failure=now;
  }

  throttle('cms_sync',()=> {
    trackEvent(success ? 'cms_sync_success' : 'cms_sync_failure',{success,dataChanged});
  },minInterval);
};

export const trackSearch=(searchTerm)=> {
  debounce(
    'search_input',
    ()=> trackEvent('search_used',{searchTerm}),
    1500
  );
};

export const trackFilter=(filter)=> {
  trackEvent('filter_selected',{filter});
};

export const trackCardClick=(websiteId,websiteTitle,category)=> {
  throttle(
    `card_${websiteId}`,
    ()=> trackEvent('card_clicked',{websiteId,websiteTitle,category})
  );
};

export const trackModalOpened=(websiteId,websiteTitle)=> {
  trackEvent('modal_opened',{websiteId,websiteTitle});
};

export const trackModalClosed=(websiteId)=> {
  trackEvent('modal_closed',{websiteId});
};

export const trackRequestSimilar=(websiteId,websiteTitle)=> {
  throttle(
    `request_${websiteId}`,
    ()=> trackEvent('request_similar_clicked',{websiteId,websiteTitle})
  );
};

export const trackFormStarted=()=> {
  throttle('form_started',()=> trackEvent('form_started'));
};

export const trackFormSubmitted=(payload)=> {
  trackEvent('form_submitted',payload);
};

export const trackFormSuccess=()=> {
  trackEvent('form_success');
};

export const trackFormError=(error)=> {
  trackEvent('form_error',{error});
};

export const activityTracker={
  trackPageView,
  trackCmsSync,
  trackSearch,
  trackFilter,
  trackCardClick,
  trackModalOpened,
  trackModalClosed,
  trackRequestSimilar,
  trackFormStarted,
  trackFormSubmitted,
  trackFormSuccess,
  trackFormError,
};

export default activityTracker;